/**
 * /jobs/new and /jobs/:jobId — shared create/edit form.
 * Mirrors legacy Create/Update fields 1-1: title, description, slug (auto),
 * locale, categories (multi), department, level, type, country, status,
 * salary, location, summary, responsible, qualification, benefit, required_skills.
 * Slug is auto-generated from title (read-only).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useJobDetail,
  useCreateJob,
  useUpdateJob,
  useAllJobCategories,
  type JobCreatePayload,
} from '@/api/jobs';
import { useEmployeesByDepartment } from '@/api/employees';

/* ── Static option lists (from legacy constants/index.ts) ──────────────── */

const LOCALE_OPTIONS = [
  { value: 'vi-VN', label: 'vi-VN' },
  { value: 'en-US', label: 'en-US' },
];

const JOB_STATUS_OPTIONS = [
  { value: 'Opened', label: 'Opened' },
  { value: 'Closed', label: 'Closed' },
];

const JOB_TYPE_OPTIONS = [
  { value: 'Permanent', label: 'Permanent' },
  { value: 'Temporary / Project', label: 'Temporary / Project' },
  { value: 'Shift / Partime', label: 'Shift / Partime' },
  { value: 'Freelance', label: 'Freelance' },
];

const JOB_LEVEL_OPTIONS = [
  { value: 'Experienced (Non - Manager)', label: 'Experienced (Non - Manager)' },
  { value: 'Executive Management', label: 'Executive Management' },
  { value: 'Senior Management', label: 'Senior Management' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Team Leader / Supervisor', label: 'Team Leader / Supervisor' },
  { value: 'Entry Level', label: 'Entry Level' },
  { value: 'Student / Internship', label: 'Student / Internship' },
];

const JOB_COUNTRY_OPTIONS = [
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Korea', label: 'Korea' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'HongKong', label: 'HongKong' },
  { value: 'Taiwan', label: 'Taiwan' },
];

/* ── Slug helper ────────────────────────────────────────────────────────── */

function generateSlug(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/* ── Shared select helper ───────────────────────────────────────────────── */

function FieldSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder ?? 'Select…'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ── Form state type ────────────────────────────────────────────────────── */

interface JobForm {
  title: string;
  description: string;
  slug: string;
  locale: string;
  categories: number[];
  department: string;
  level: string;
  type: string;
  country: string;
  status: string;
  salary: string;
  location: string;
  summary: string;
  responsible: string;
  qualification: string;
  benefit: string;
  required_skills: string;
}

const EMPTY_FORM: JobForm = {
  title: '',
  description: '',
  slug: '',
  locale: 'vi-VN',
  categories: [],
  department: '',
  level: '',
  type: 'Permanent',
  country: 'Vietnam',
  status: '',
  salary: '',
  location: '',
  summary: '',
  responsible: '',
  qualification: '',
  benefit: '',
  required_skills: '',
};

/* ── Grid layouts ────────────────────────────────────────────────────────── */

const twoCol = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--vn-space-4)',
} as const;

/* ── Component ──────────────────────────────────────────────────────────── */

export default function JobFormPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const isEdit = !!jobId;
  const id = Number(jobId);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState<JobForm>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState('');

  /* ── Data fetching ─────────────────────────────────────────────────────── */
  const detailQ = useJobDetail(isEdit ? id : 0);
  const categoriesQ = useAllJobCategories();

  const categoryOptions = useMemo(
    () =>
      (categoriesQ.data?.results ?? []).map((c) => ({
        value: String(c.id),
        label: c.title,
      })),
    [categoriesQ.data],
  );

  /* Department options from the live departments API (legacy used department_name). */
  const departmentsQ = useEmployeesByDepartment();
  const departmentOptions = useMemo(() => {
    const opts = (departmentsQ.data ?? [])
      .map((d) => ({ value: d.department_name, label: d.department_name }))
      .filter((o) => o.value);
    // Preserve an existing value that isn't in the fetched list (avoids silent loss on edit).
    if (form.department && !opts.some((o) => o.value === form.department)) {
      opts.unshift({ value: form.department, label: form.department });
    }
    return opts;
  }, [departmentsQ.data, form.department]);

  /* Prefill form when editing */
  useEffect(() => {
    const job = detailQ.data?.result;
    if (!job) return;
    setForm({
      title: job.title ?? '',
      description: job.description ?? '',
      slug: job.slug ?? '',
      locale: job.locale ?? 'vi-VN',
      categories: Array.isArray(job.categories) ? job.categories : [],
      department: job.department ?? '',
      level: job.level ?? '',
      type: job.type ?? 'Permanent',
      country: job.country ?? 'Vietnam',
      status: job.status ?? '',
      salary: job.salary ?? '',
      location: job.location ?? '',
      summary: job.summary ?? '',
      responsible: job.responsible ?? '',
      qualification: job.qualification ?? '',
      benefit: job.benefit ?? '',
      required_skills: job.required_skills ?? '',
    });
  }, [detailQ.data]);

  /* Auto-generate slug from title (create mode only) */
  useEffect(() => {
    if (!isEdit) {
      setForm((prev) => ({ ...prev, slug: generateSlug(prev.title) }));
    }
  }, [form.title, isEdit]);

  /* ── Mutations ─────────────────────────────────────────────────────────── */
  const createMut = useCreateJob();
  const updateMut = useUpdateJob(id);
  const isPending = createMut.isPending || updateMut.isPending;

  /* ── Helpers ────────────────────────────────────────────────────────────── */
  const set = <K extends keyof JobForm>(key: K, value: JobForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    const required: (keyof JobForm)[] = [
      'title', 'location', 'status', 'locale', 'type',
      'benefit', 'responsible', 'qualification', 'description',
      'level', 'country', 'salary', 'department',
    ];
    const missing = required.filter((k) => !form[k]);
    if (missing.length > 0) {
      setSubmitError(`Please fill in required fields: ${missing.join(', ')}`);
      return;
    }
    if (form.categories.length === 0) {
      setSubmitError('Please select at least one category.');
      return;
    }

    const payload: JobCreatePayload = {
      title: form.title,
      location: form.location,
      status: form.status,
      locale: form.locale,
      type: form.type,
      summary: form.summary,
      benefit: form.benefit,
      responsible: form.responsible,
      qualification: form.qualification,
      description: form.description,
      level: form.level,
      country: form.country,
      salary: form.salary,
      categories: form.categories,
      department: form.department,
      required_skills: form.required_skills || undefined,
    };

    try {
      if (isEdit) {
        await updateMut.mutateAsync(payload);
        toast.success('Job updated successfully.');
      } else {
        await createMut.mutateAsync(payload);
        toast.success('Job created successfully.');
        navigate('/jobs');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error ??
        (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message ??
        'An unexpected error occurred.';
      setSubmitError(msg);
    }
  };

  /* ── Loading / error (edit mode) ───────────────────────────────────────── */
  if (isEdit && detailQ.isLoading) {
    return (
      <Container size="lg">
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 40 }} />
          ))}
        </div>
      </Container>
    );
  }

  if (isEdit && detailQ.isError) {
    return (
      <Container size="lg">
        <Alert
          tone="danger"
          title="Failed to load job"
          actions={
            <Button size="sm" variant="ghost" onClick={() => detailQ.refetch()}>
              Retry
            </Button>
          }
        />
      </Container>
    );
  }

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <Container size="lg">
      <PageHeader
        title={isEdit ? 'Edit Job' : 'New Job'}
        description={isEdit ? `Editing job #${id}` : 'Create a new job posting.'}
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <Button
            variant="secondary"
            startIcon={<ArrowLeft size={15} />}
            onClick={() => navigate('/jobs')}
          >
            Back to Jobs
          </Button>
        }
      />

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Basic info */}
          <Card>
            <CardHeader>
              <CardTitle>Job Information</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <div style={twoCol}>
                  <Field label="Job Title" required>
                    {(p) => (
                      <Input
                        {...p}
                        value={form.title}
                        onChange={(e) => set('title', e.target.value)}
                        placeholder="Enter job title"
                      />
                    )}
                  </Field>
                  <Field label="Description">
                    {(p) => (
                      <Input
                        {...p}
                        value={form.description}
                        onChange={(e) => set('description', e.target.value)}
                        placeholder="Short description"
                      />
                    )}
                  </Field>
                </div>

                <div style={twoCol}>
                  <Field label="Slug" description="Auto-generated from title">
                    {(p) => (
                      <Input
                        {...p}
                        value={form.slug}
                        readOnly
                        disabled
                        style={{ color: 'var(--vn-fg-muted)' }}
                      />
                    )}
                  </Field>
                  <Field label="Locale" required>
                    {(p) => (
                      <FieldSelect
                        id={p.id}
                        value={form.locale}
                        onChange={(v) => set('locale', v)}
                        options={LOCALE_OPTIONS}
                        placeholder="Select locale"
                      />
                    )}
                  </Field>
                </div>

                <div style={twoCol}>
                  <Field label="Categories" required>
                    {(p) => (
                      <MultiSelect
                        placeholder="Select categories"
                        options={categoryOptions}
                        value={form.categories.map(String)}
                        onChange={(vals) => set('categories', vals.map(Number))}
                        aria-describedby={p['aria-describedby']}
                      />
                    )}
                  </Field>
                  <Field label="Department" required>
                    {(p) => (
                      <FieldSelect
                        id={p.id}
                        value={form.department}
                        onChange={(v) => set('department', v)}
                        options={departmentOptions}
                        placeholder={
                          departmentsQ.isLoading ? 'Loading departments…' : 'Select department'
                        }
                        disabled={departmentsQ.isLoading}
                      />
                    )}
                  </Field>
                </div>

                <div style={twoCol}>
                  <Field label="Level" required>
                    {(p) => (
                      <FieldSelect
                        id={p.id}
                        value={form.level}
                        onChange={(v) => set('level', v)}
                        options={JOB_LEVEL_OPTIONS}
                        placeholder="Select level"
                      />
                    )}
                  </Field>
                  <Field label="Type" required>
                    {(p) => (
                      <FieldSelect
                        id={p.id}
                        value={form.type}
                        onChange={(v) => set('type', v)}
                        options={JOB_TYPE_OPTIONS}
                        placeholder="Select type"
                      />
                    )}
                  </Field>
                </div>

                <div style={twoCol}>
                  <Field label="Country" required>
                    {(p) => (
                      <FieldSelect
                        id={p.id}
                        value={form.country}
                        onChange={(v) => set('country', v)}
                        options={JOB_COUNTRY_OPTIONS}
                        placeholder="Select country"
                      />
                    )}
                  </Field>
                  <Field label="Status" required>
                    {(p) => (
                      <FieldSelect
                        id={p.id}
                        value={form.status}
                        onChange={(v) => set('status', v)}
                        options={JOB_STATUS_OPTIONS}
                        placeholder="Select status"
                      />
                    )}
                  </Field>
                </div>

                <div style={twoCol}>
                  <Field label="Salary" required>
                    {(p) => (
                      <Textarea
                        {...p}
                        value={form.salary}
                        onChange={(e) => set('salary', e.target.value)}
                        placeholder="e.g. Negotiable / 1,000–2,000 USD"
                        rows={2}
                      />
                    )}
                  </Field>
                  <Field label="Location" required>
                    {(p) => (
                      <Textarea
                        {...p}
                        value={form.location}
                        onChange={(e) => set('location', e.target.value)}
                        placeholder="e.g. Ho Chi Minh City"
                        rows={2}
                      />
                    )}
                  </Field>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Detailed content */}
          <Card>
            <CardHeader>
              <CardTitle>Job Content</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <Field label="Summary">
                  {(p) => (
                    <Textarea
                      {...p}
                      value={form.summary}
                      onChange={(e) => set('summary', e.target.value)}
                      placeholder="Brief overview of the role"
                      rows={3}
                    />
                  )}
                </Field>
                <Field label="Responsibilities" required>
                  {(p) => (
                    <Textarea
                      {...p}
                      value={form.responsible}
                      onChange={(e) => set('responsible', e.target.value)}
                      placeholder="Key responsibilities…"
                      rows={5}
                    />
                  )}
                </Field>
                <Field label="Qualifications" required>
                  {(p) => (
                    <Textarea
                      {...p}
                      value={form.qualification}
                      onChange={(e) => set('qualification', e.target.value)}
                      placeholder="Required qualifications…"
                      rows={5}
                    />
                  )}
                </Field>
                <Field label="Benefits" required>
                  {(p) => (
                    <Textarea
                      {...p}
                      value={form.benefit}
                      onChange={(e) => set('benefit', e.target.value)}
                      placeholder="Compensation and perks…"
                      rows={5}
                    />
                  )}
                </Field>
                <Field label="Required Skills">
                  {(p) => (
                    <Textarea
                      {...p}
                      value={form.required_skills}
                      onChange={(e) => set('required_skills', e.target.value)}
                      placeholder="e.g. React, TypeScript, Node.js"
                      rows={3}
                    />
                  )}
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Error + submit */}
          {submitError && (
            <Alert tone="danger" title="Submission failed">
              {submitError}
            </Alert>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate('/jobs')}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" startIcon={<Save size={15} />} disabled={isPending}>
              {isPending ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create job')}
            </Button>
          </div>
        </div>
      </form>
    </Container>
  );
}
